/**
 * Copyright (c) eventlint. All Rights Reserved.
 *
 *
 * @summary
 * @author Segev {CJ} Shmueli <cj@segevs.com>
 *
 * Created: 7/6/2022
 */

import React, { FC } from 'react';
import data from '../data/updates.json';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import { Card, CardHeader, CardContent, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';
import { DateTime } from 'luxon';
import { Chart } from "react-google-charts";

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
    height: 10,
    borderRadius: 5,
    [`&.${linearProgressClasses.colorPrimary}`]: {
        backgroundColor: theme.palette.grey[theme.palette.mode === 'light' ? 200 : 800],
    },
    [`& .${linearProgressClasses.bar}`]: {
        borderRadius: 5,
        backgroundColor: theme.palette.mode === 'light' ? '#1a90ff' : '#308fe8',
    },
}));


const formatDateRange = (date: string) => {
    const d = DateTime.fromFormat(date, 'yyyy-MM-dd');

    return `${d.plus({ days: -6 }).toFormat('MMM d')} - ${d.toFormat('MMM d')} ${d.toFormat('yyyy')}`;
}

const TitledLine = ({ title, details }: { title: string, details: string }) => {
    if ((details ?? "") === "") return null;
    return (<>
        <div className='py-3'>
            <div className='font-semibold'>{title}</div>
            {/* <Typography variant='h6' ></Typography> */}
            <Typography className='text-gray-500' variant='body1' >{details}</Typography>
        </div>
    </>);
}


const Goals: FC<{ goals: { title: string; completed: number }[] }> = ({ goals }) => {
    return (<>
        <div>
            <Typography variant='h5'>Goals for the next week</Typography>
            {goals.map((goal, index) => <div key={`ind-${index}`} className='flex flex-row items-center gap-3'>
                <div style={{ width: '20%' }}>
                    <BorderLinearProgress variant="determinate" value={goal.completed * 100} />
                </div>
                <Typography variant='body1' className='text-gray-500'>{goal.title}</Typography>
            </div>
            )}
        </div>
    </>);

}


const Scores: FC<{ update: any }> = ({ update }) => {
    return (<>
        <div>
            <div className='flex text-center font-bold border-b-2'>
                {update.weeksToLaunch > 0 &&
                    <div className='w-1/3'>WTL</div>}
                {(update.primaryMatrix ?? []).map((item: any) => <div key={item.key} className='w-1/3'>{item.key}</div>)}
                <div className='w-1/3'>Morale</div>
                <div className='w-1/3'>Users talked to</div>
            </div>
            <div className='flex text-center'>
                {update.weeksToLaunch > 0 && <div className='w-1/3'>{update.weeksToLaunch}</div>}
                {(update.primaryMatrix ?? []).map((item: any) => <div key={item.key} className='w-1/3'>{item.value}</div>)}
                <div className='w-1/3'>{update.morale}</div>
                <div className='w-1/3'>{update.usersTalkedTo}</div>
            </div>
        </div>
    </>);

}


const _dateForChart = (date: string) => {
    const d = DateTime.fromFormat(date, 'yyyy-MM-dd');
    return d.toJSDate();
}

// const _formatDateForChart = (date: string) => {
//     const d = DateTime.fromFormat(date, 'yyyy-MM-dd');
//     return d.toFormat('MMM, dd');
// }

const HomePage = () => {
    const [activeIndex, setActiveIndex] = React.useState(0);

    const arrayForCharts = [...data.updates];
    arrayForCharts.reverse();

    return (<>
        <div className='hero'>
            <h1>Weekly Updates</h1>
        </div>
        <main>

            <div className='flex '>


                <div className='flex-grow' style={{
                    maxWidth: '800px',
                    margin: 'auto',
                    gap: '20px',
                    display: 'flex',
                    padding: '0 1.5em',
                    flexDirection: 'column',
                }}>


                    {data.updates.map((update: any, index: number) => (
                        <div key={`update-${index}`}>
                            <Card className={`${index === activeIndex ? 'visible' : ''}`} onClick={() => setActiveIndex(index)} >
                                <CardHeader

                                    className='cursor-pointer'
                                    title={formatDateRange(update.endDate)}
                                    titleTypographyProps={{
                                        variant: 'h6',
                                    }}
                                    style={{
                                        background: '#efefef',
                                    }}
                                />
                                <CardContent className={`${index === activeIndex ? '' : 'hidden'}`}>
                                    <Scores update={update} />
                                    <div className='p-4 bg-slate-50 my-4'>
                                        <TitledLine title="Highlights" details={update.highlights} />
                                    </div>

                                    <div>
                                        <Goals goals={update.goals} />
                                    </div>
                                    <Divider />
                                    <TitledLine title="Learned from users?" details={update.learnedFromUsers} />
                                    <TitledLine title="Most improved primary metric?" details={update.mostImprovedPrimaryMetric} />
                                    <TitledLine title="Biggest obstacle?" details={update.obstacle} />
                                </CardContent>
                            </Card>
                        </div>
                    ))}
                </div>
                <div className='flex flex-col'>
                    <Chart
                        chartType="LineChart"
                        data={[['', 'WTL - Weeks to launch'], ...arrayForCharts.map((d, i) => [_dateForChart(d.endDate), d.weeksToLaunch])]}
                        width="400px"
                        height="400px"
                        options={{
                            title: "Primary Metric",
                            legend: { position: "top", maxLines: 3 },
                            vAxis: {
                                minValue: 0,
                                maxValue: 10,
                                ticks: [8, 6, 4, 2, 0],
                            },
                            hAxis: {
                                format: 'MMM d',
                            },
                            curveType: 'function',
                            pointSize: 5,
                        }}
                        legendToggle
                    />
                    <Chart
                        chartType="LineChart"
                        data={[
                            ["", "Morale", "Users contacted"], ...arrayForCharts.map((d, i) => [_dateForChart(d.endDate), d.morale, Number(d.usersTalkedTo)])
                        ]}
                        options={{
                            title: "Secondary Metrics",

                            legend: { position: "top", maxLines: 3 },
                            vAxis: {
                                minValue: 0,
                                maxValue: 10,
                                ticks: [0, 5, 10],
                            },
                            hAxis: {
                                format: 'MMM d',
                            },
                            curveType: 'function',
                            pointSize: 5,
                        }}

                        width="400px"
                        height="400px"
                        legendToggle
                    />

                </div>
            </div>
        </main>
    </>);
}

export default HomePage;